import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { I18nProvider } from "./i18n";
import { Layout } from "./components/Layout";
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import TaperTool from "./pages/TaperTool";
import GPLetter from "./pages/GPLetter";
import Locator from "./pages/Locator";
import Tracker from "./pages/Tracker";
import Education from "./pages/Education";
import Admin from "./pages/Admin";
import Buddy from "./pages/Buddy";
import Forum from "./pages/Forum";
import Reminders from "./pages/Reminders";
import WeeklySummary from "./pages/WeeklySummary";
import AuthCallback from "./pages/AuthCallback";
import ResetPassword from "./pages/ResetPassword";
import Articles from "./pages/Articles";
import ArticleDetail from "./pages/ArticleDetail";
import PublicTaper from "./pages/PublicTaper";
import PublicFindSupport from "./pages/PublicFindSupport";
import Contact from "./pages/Contact";
import AccountSettings from "./pages/AccountSettings";
import MyCentre from "./pages/MyCentre";

const Loader = () => (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <div className="h-12 w-12 animate-pulse rounded-full bg-[#4A7c59]/30" />
  </div>
);

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <Loader />;
  if (!user) return <Navigate to="/auth" replace />;
  return <Layout>{children}</Layout>;
}

function PublicOnly({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <Loader />;
  if (user) return <Navigate to="/app" replace />;
  return children;
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, hash]);
  return null;
}

function App() {
  return (
    <div className="App">
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/auth" element={<PublicOnly><Auth /></PublicOnly>} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/auth/reset" element={<ResetPassword />} />
              <Route path="/articles" element={<Articles />} />
              <Route path="/articles/:slug" element={<ArticleDetail />} />
              <Route path="/taper" element={<PublicTaper />} />
              <Route path="/find-support" element={<PublicFindSupport />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/app" element={<Protected><Dashboard /></Protected>} />
              <Route path="/app/taper" element={<Protected><TaperTool /></Protected>} />
              <Route path="/app/taper/letter" element={<Protected><GPLetter /></Protected>} />
              <Route path="/app/locator" element={<Protected><Locator /></Protected>} />
              <Route path="/app/tracker" element={<Protected><Tracker /></Protected>} />
              <Route path="/app/tracker/summary" element={<Protected><WeeklySummary /></Protected>} />
              <Route path="/app/reminders" element={<Protected><Reminders /></Protected>} />
              <Route path="/app/buddy" element={<Protected><Buddy /></Protected>} />
              <Route path="/app/community" element={<Protected><Forum /></Protected>} />
              <Route path="/app/learn" element={<Protected><Education /></Protected>} />
              <Route path="/app/admin" element={<Protected><Admin /></Protected>} />
              <Route path="/app/settings" element={<Protected><AccountSettings /></Protected>} />
              <Route path="/app/my-centre" element={<Protected><MyCentre /></Protected>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="bottom-center" richColors />
        </AuthProvider>
      </I18nProvider>
    </div>
  );
}

export default App;
